import { usePlayer } from '../../contexts/PlayerContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function MiniPlayer() {
    const player = usePlayer();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    if (!player.isActive || !player.currentTrackData) return null;

    // Hide on the guide page for the currently playing attraction
    if (location.pathname === `/guide/${player.attractionId}`) return null;

    const track = player.currentTrackData;
    const imgSrc = track.image_url
        ? (track.image_url.startsWith('http') ? track.image_url : `${API}${track.image_url}`)
        : null;
    const progress = player.duration > 0 ? (player.currentTime / player.duration) * 100 : 0;
    const isTraveler = user?.role === 'traveler';

    return (
        <div className={`fixed ${isTraveler ? 'bottom-16 lg:bottom-0' : 'bottom-0'} left-0 right-0 z-50 animate-slide-up`} data-testid="mini-player">
            {/* Progress bar */}
            <div className="h-1 bg-white/5">
                <div
                    className="h-full bg-gold transition-all duration-300"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <div className="glass border-t border-white/10 safe-area-pb">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
                    {/* Track Image */}
                    <button
                        onClick={() => navigate(`/guide/${player.attractionId}`)}
                        className="relative w-12 h-12 rounded-lg overflow-hidden shrink-0 group"
                        data-testid="mini-player-art"
                    >
                        {imgSrc ? (
                            <img src={imgSrc} alt={track.title} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-gold/20 flex items-center justify-center">
                                <Play className="w-5 h-5 text-gold" />
                            </div>
                        )}
                    </button>

                    {/* Track Info */}
                    <button
                        onClick={() => navigate(`/guide/${player.attractionId}`)}
                        className="flex-1 min-w-0 text-left"
                        data-testid="mini-player-info"
                    >
                        <p className="text-sm text-white font-medium truncate">{track.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{player.attractionTitle}</p>
                    </button>

                    {/* Time */}
                    <span className="text-xs text-muted-foreground hidden sm:block w-20 text-center font-mono">
                        {formatTime(player.currentTime)} / {formatTime(player.duration)}
                    </span>

                    {/* Controls */}
                    <div className="flex items-center gap-1">
                        <button
                            onClick={player.prevTrack}
                            disabled={player.currentTrack === 0}
                            className="p-2 text-muted-foreground hover:text-white disabled:opacity-30 transition-colors"
                            data-testid="mini-player-prev"
                        >
                            <SkipBack className="w-5 h-5" />
                        </button>
                        <button
                            onClick={player.togglePlay}
                            className="w-10 h-10 rounded-full bg-gold flex items-center justify-center hover:bg-gold/90 transition-colors"
                            data-testid="mini-player-play-btn"
                        >
                            {player.playing ? (
                                <Pause className="w-5 h-5 text-[#0A0D14]" />
                            ) : (
                                <Play className="w-5 h-5 text-[#0A0D14] ml-0.5" />
                            )}
                        </button>
                        <button
                            onClick={player.nextTrack}
                            disabled={player.currentTrack >= player.tracks.length - 1}
                            className="p-2 text-muted-foreground hover:text-white disabled:opacity-30 transition-colors"
                            data-testid="mini-player-next"
                        >
                            <SkipForward className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Dismiss */}
                    <button
                        onClick={player.dismiss}
                        className="p-2 text-muted-foreground hover:text-white transition-colors"
                        title="Close player"
                        data-testid="mini-player-dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
